import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Input, FormInstance, Row, theme, Col, Skeleton, Divider } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';

import { useStores } from '@/hooks/use-stores';
import { useResponsive } from '@/hooks/useResponsive';
import { DeviceType, compressorTypes } from '@/constants/deviceTypeEnum';
import { EPSelect, EPSelectOption } from '@/components';
import BoosterWidget from '@/comp/common/icons/BoosterWidget';

import { Entity } from '../../../NodeWidget.types';
import {
  getDeprecatedModelToolTip,
  getFieldSelectProps,
  IsDeprecatedModel,
  IsDisconnectedDevice,
  useFormHelper,
} from '../../EditAssetDrawer.utils';
import { useInitialValues, mapFormValuesToPayload } from './EditBoosterForm.utils';
import { EditBoosterFormContainer } from './EditBoosterForm.styles';
import ConnectionSelector from '../ConnectionSelector/ConnectionSelector';
import ConnectionList from '../ConnectionList/ConnectionList';

const EditBoosterForm = observer(
  ({
    entity,
    form,
    afterOpenChange,
    messageApi,
  }: {
    entity: Entity;
    form: FormInstance;
    afterOpenChange: boolean;
    messageApi: MessageInstance;
  }) => {
    const { t } = useTranslation();
    const { pidEditStore } = useStores();
    const { isMobile } = useResponsive();
    const { token } = theme.useToken();
    const deprecatedModel = IsDeprecatedModel(entity.deviceKind);
    const { deviceModelOptions, isLoadingModels, onFinish, onModelSearch, deviceConnectionOptions, initialValues } =
      useFormHelper(t, entity, afterOpenChange, messageApi, mapFormValuesToPayload, useInitialValues);

    const currentModelId = Form.useWatch('deviceModelId', form);
    const disconnectedDevice = IsDisconnectedDevice(deviceModelOptions, currentModelId);

    const isLoading = !afterOpenChange || isLoadingModels;

    if (isLoading) {
      return <Skeleton active paragraph={{ rows: 6 }} />;
    }

    return (
      <EditBoosterFormContainer>
        <Form
          layout="vertical"
          form={form}
          initialValues={initialValues}
          onFinish={onFinish}
          disabled={pidEditStore.isLoadingDraft || pidEditStore.isLoadingModelSpec}
        >
          <span className="horizontal-label">{t('connect')}</span>
          <Row className="connected-to-row">
            <Col span={6}>
              <Form.Item label=" " name="connectFrom">
                <ConnectionSelector
                  label="from"
                  options={deviceConnectionOptions.filter(option => option.deviceType !== DeviceType.ProductionFloor)}
                  form={form}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <div className="pipe" />
            </Col>
            <Col span={6} className="widget">
              <BoosterWidget width={100} height={82} color={token.colorBorder} />
            </Col>
            <Col span={3}>
              <div className="pipe" />
            </Col>
            <Col span={6}>
              <Form.Item label=" " name="connectTo">
                <ConnectionSelector
                  label="to"
                  options={deviceConnectionOptions.filter(option => !compressorTypes.includes(option.deviceType))}
                  form={form}
                />
              </Form.Item>
            </Col>
          </Row>
          <ConnectionList form={form} field="from" />
          <ConnectionList form={form} field="to" />
          <Divider className="divider" />
          <Form.Item label={t('name')} name="name">
            <Input size="large" />
          </Form.Item>
          <Form.Item
            label={t('modelSpec')}
            name="deviceModelId"
            tooltip={deprecatedModel ? getDeprecatedModelToolTip() : undefined}
            required
            rules={[{ required: true, message: t('required') }]}
          >
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectModelSpec'), 'deviceModelId')}
              loading={isLoadingModels}
              onSearch={onModelSearch}
              filterOption={false}
              disabled={deprecatedModel}
            >
              {deviceModelOptions.map(option => (
                <EPSelectOption key={option.value} value={option.value}>
                  {option.label}
                </EPSelectOption>
              ))}
            </EPSelect>
          </Form.Item>
          <Form.Item label={t('controller')} name="controllerId" rules={[{ required: !disconnectedDevice, message: t('required') }]}>
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectController'), 'controllerId')}
              disabled={disconnectedDevice}
            >
              {pidEditStore.deviceControllers.map(option => (
                <EPSelectOption key={option.value} value={option.value}>
                  {option.label}
                </EPSelectOption>
              ))}
            </EPSelect>
          </Form.Item>
        </Form>{' '}
      </EditBoosterFormContainer>
    );
  },
);

export default EditBoosterForm;
